// NOTE: TBH I don't think we're going to have backend support for showing % progres for publish lol so don't need to review worry about this component that might not even be used

import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';

interface PublishingLoaderModalProps {
  isModalOpen: boolean;
  setIsModalOpen: (open: boolean) => void;
  workflowName: string;
  onCancel: () => void;
  onComplete?: () => void;
  progress?: number; // External progress control
}

export const PublishingLoaderModal: React.FC<PublishingLoaderModalProps> = ({
  isModalOpen,
  setIsModalOpen,
  workflowName,
  onCancel,
  onComplete,
  progress: externalProgress,
}) => {
  const [internalProgress, setInternalProgress] = useState(0);

  // Use external progress if provided, otherwise use internal simulation
  const currentProgress =
    externalProgress !== undefined ? externalProgress : internalProgress;

  // Reset progress when modal opens/closes
  useEffect(() => {
    if (!isModalOpen) {
      setInternalProgress(0);
      return;
    }

    // Only run internal simulation if no external progress is provided
    if (externalProgress !== undefined) {
      return;
    }

    const timer = setInterval(() => {
      setInternalProgress((prevProgress) => {
        // Simulate realistic publishing progress with variable speed
        let increment;
        if (prevProgress < 30) {
          // Quick start
          increment = Math.random() * 10 + 8; // 8-18% increment
        } else if (prevProgress < 70) {
          // Slower middle section
          increment = Math.random() * 8 + 3; // 3-11% increment
        } else if (prevProgress < 95) {
          // Slow near completion
          increment = Math.random() * 5 + 1; // 1-6% increment
        } else {
          // Final push to 100%
          increment = Math.random() * 3 + 2; // 2-5% increment
        }

        const newProgress = Math.min(prevProgress + increment, 100);

        // Auto-close when complete (only for internal simulation)
        if (newProgress >= 100) {
          setTimeout(() => {
            setIsModalOpen(false);
            onComplete?.(); // Call completion callback
          }, 800); // Brief delay to show 100% completion
        }

        return newProgress;
      });
    }, 600); // Update every 600ms for smooth animation

    return () => {
      clearInterval(timer);
    };
  }, [isModalOpen, setIsModalOpen, onComplete, externalProgress]);

  // Handle completion when external progress reaches 100%
  useEffect(() => {
    if (externalProgress === 100) {
      setTimeout(() => {
        setIsModalOpen(false);
        onComplete?.(); // Call completion callback
      }, 800); // Brief delay to show 100% completion
    }
  }, [externalProgress, setIsModalOpen, onComplete]);

  const handleCancel = () => {
    onCancel();
    setIsModalOpen(false);
    setInternalProgress(0);
  };

  return (
    <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
      <DialogContent className='w-[416px] p-6 fixed top-20 right-4 left-auto transform-none translate-x-0 translate-y-0 z-50'>
        <DialogTitle className='text-sm font-medium text-foreground'>
          Publishing "{workflowName}"
        </DialogTitle>
        <div className='flex flex-col gap-6'>
          <div className='flex flex-col gap-2'>
            <div className='w-full'>
              <Progress
                value={currentProgress}
                className='w-full h-2 bg-gray-200 dark:bg-gray-700'
              />
            </div>
          </div>

          <div className='flex justify-start'>
            <Button
              variant='outline'
              onClick={handleCancel}
              className='px-4 py-2'
            >
              Cancel
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
